import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { BackIcon } from './icons';

/** Screen header: optional back button, title, optional actions on the right. */
export function ScreenHeader({
  title,
  back,
  actions,
}: {
  title: ReactNode;
  back?: string | true;
  actions?: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <header className="mx-auto box-border flex h-14 w-full max-w-[720px] shrink-0 items-center gap-1 px-1.5">
      {back && (
        <button
          type="button"
          aria-label="Back"
          onClick={() => (back === true ? void navigate(-1) : void navigate(back))}
          className="flex h-11 w-11 items-center justify-center rounded-btn border-0 bg-transparent p-0 text-ink"
        >
          <BackIcon />
        </button>
      )}
      <h1 className={`m-0 flex-1 text-[18px] font-bold ${back ? '' : 'px-2.5'}`}>{title}</h1>
      {actions}
    </header>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-line bg-surface ${className}`}>{children}</div>
  );
}

/** A friendly placeholder for screens that arrive in a later stage. */
export function EmptyState({ title, body }: { title: string; body: ReactNode }) {
  return (
    <Card className="flex flex-col gap-1.5 px-4 py-5">
      <h2 className="m-0 text-[16px] font-bold">{title}</h2>
      <div className="text-[14px] text-muted">{body}</div>
    </Card>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="mx-1 my-0 text-[13px] font-semibold text-muted">{children}</h2>;
}

/** Segmented control (Settings mockup): one choice of a few. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  /** `disabled`: shown, but it can't be chosen. */
  options: readonly { value: T; label: string; disabled?: boolean }[];
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid gap-1 rounded-[12px] bg-segment p-1"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={`h-11 rounded-[9px] border-0 text-[14px] disabled:opacity-45 ${
              checked
                ? 'bg-surface font-semibold text-ink shadow-[0_1px_2px_rgba(31,30,28,0.14)]'
                : 'bg-transparent font-medium text-secondary'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A row with an on/off switch (Settings mockup). */
export function SwitchRow({
  label,
  checked,
  onChange,
  last = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  last?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`flex min-h-14 items-center gap-3 border-0 bg-transparent px-3.5 text-left text-[15px] text-ink ${
        last ? '' : 'border-b border-solid border-line-soft'
      }`}
    >
      <span className="flex-1">{label}</span>
      <span
        className={`box-border flex h-7 w-[46px] rounded-[14px] p-[3px] ${
          checked ? 'justify-end bg-accent' : 'justify-start bg-toggle-off'
        }`}
      >
        <span className="h-[22px] w-[22px] rounded-[11px] bg-surface shadow-[0_1px_2px_rgba(31,30,28,0.2)]" />
      </span>
    </button>
  );
}
